/**
 * Line-level diff helper for the branching code views (KAction SQL, KDatasource C#/JS).
 * Pure and React-free so it is unit-testable in isolation.
 */

/**
 * Indices of `lines` that are NOT part of a longest-common-subsequence with `compareLines` — i.e.
 * lines unique to this side (added when rendering the "after" text, removed when rendering "before").
 * This replaces a naive positional comparison, which mislabels every line once one is inserted or
 * removed (everything below shifts and reads as changed).
 */
export function changedLineIndices(lines: string[], compareLines: string[]): Set<number> {
  const n: number = lines.length;
  const m: number = compareLines.length;
  const changed: Set<number> = new Set<number>();

  // Guard against pathological sizes; code blocks are normally short (and truncated when collapsed).
  if (n === 0 || m === 0 || n > 600 || m > 600) {
    for (let i = 0; i < n; i++) {
      if (lines[i] !== compareLines[i]) changed.add(i);
    }
    return changed;
  }

  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = lines[i] === compareLines[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const matched: Set<number> = new Set<number>();
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (lines[i] === compareLines[j]) {
      matched.add(i);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i++;
    } else {
      j++;
    }
  }

  for (let k = 0; k < n; k++) {
    if (!matched.has(k)) changed.add(k);
  }
  return changed;
}
